import React, { useCallback, useEffect, useRef, useState } from "react";
import { transformToHtml } from "../../../utils/CommonUtils";
import {
  fetchFlightStatusAutosuggest,
  normalizeAutosuggestIata,
} from "../../../utils/flightStatusAutosuggestClient";

function getDisplayName(item, displayType) {
  const cityName = String(item?.cityName || "").trim();
  const country = String(item?.country || "").trim();
  const airportName = String(item?.airportName || "").trim();
  if (displayType === "airport") {
    return airportName || [cityName, country].filter(Boolean).join(", ");
  }
  return [cityName, country].filter(Boolean).join(", ") || airportName;
}

function IataPill({ item }) {
  const iata = normalizeAutosuggestIata(item?.iata);
  if (!iata) return null;
  const borderColor = item?.iataBorderColors?.[0] || "#D8D8D8";
  const backgroundColor = item?.iataBgColors?.[0] || "#F2F2F2";
  return (
    <span
      className="fs-autosuggest__iataPill fontSize12"
      style={{ borderColor, backgroundColor }}
    >
      {iata}
    </span>
  );
}

function SuggestionTag({ tag }) {
  if (!tag?.text) return null;
  const backgroundColor = tag?.bgColors?.[0] || "#EFF1FF";
  return (
    <span className="fs-autosuggest__tag fontSize12" style={{ backgroundColor }}>
      {transformToHtml(tag.text)}
    </span>
  );
}

function SuggestionRow({ item, displayType, nearby = false, onSelect }) {
  const iata = normalizeAutosuggestIata(item?.iata);
  if (!iata) return null;
  const title = getDisplayName(item, nearby ? "airport" : "city");
  const subTitle = nearby
    ? String(item?.distanceInfoText || item?.airportName || "").trim()
    : String(item?.airportName || "").trim();

  return (
    <li
      className={`fs-autosuggest__item${nearby ? " fs-autosuggest__item--nearby" : ""}`}
      role="option"
      tabIndex={0}
      aria-selected="false"
      onClick={() => onSelect(item)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(item);
        }
      }}
    >
      <IataPill item={item} />
      <div className="fs-autosuggest__itemText">
        <div className="fs-autosuggest__itemTitle fontSize14">{title}</div>
        {subTitle ? <div className="fs-autosuggest__itemSubTitle fontSize12">{subTitle}</div> : null}
      </div>
      {!nearby ? <SuggestionTag tag={item?.tag} /> : null}
    </li>
  );
}

export default function FlightAutosuggestField({
  field,
  fieldConfig,
  codeValue = "",
  displayValue = "",
  displayType = "city",
  isOpen,
  onOpen,
  onClose,
  onSelect,
}) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [title, setTitle] = useState("SUGGESTIONS");
  const [subTitle, setSubTitle] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const latestRequestRef = useRef(0);
  const searchInputRef = useRef(null);

  const loadSuggestions = useCallback(async (searchTerm) => {
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;
    setIsLoading(true);
    try {
      const result = await fetchFlightStatusAutosuggest({ query: searchTerm });
      if (latestRequestRef.current !== requestId) return;
      setSuggestions(result.data || []);
      setTitle(result.title || "SUGGESTIONS");
      setSubTitle(result.subTitle || "");
    } catch {
      if (latestRequestRef.current !== requestId) return;
      setSuggestions([]);
      setTitle("SUGGESTIONS");
      setSubTitle("");
    } finally {
      if (latestRequestRef.current === requestId) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const timer = setTimeout(() => {
      loadSuggestions(query.trim());
    }, 250);
    return () => clearTimeout(timer);
  }, [isOpen, query, loadSuggestions]);

  useEffect(() => {
    if (!isOpen) {
      latestRequestRef.current += 1;
      setQuery("");
      setSuggestions([]);
      return undefined;
    }
    const timer = setTimeout(() => searchInputRef.current?.focus(), 0);
    return () => clearTimeout(timer);
  }, [isOpen]);

  const handleSelect = (item) => {
    const iata = normalizeAutosuggestIata(item?.iata);
    if (!iata) return;
    onSelect({
      code: iata,
      displayValue: getDisplayName(item, displayType),
      item,
    });
    onClose();
  };

  const shownText = displayValue || codeValue;

  return (
    <div className={`fs-header__field fs-autosuggest${isOpen ? " fs-autosuggest--open" : ""}`}>
      {fieldConfig?.label && (
        <div className="fs-header__field-label fontSize12">
          {transformToHtml(fieldConfig.label)}
        </div>
      )}
      <button
        type="button"
        className="fs-header__field-input fs-header__field-input--autocomplete fs-autosuggest__trigger fontSize14"
        onClick={onOpen}
        aria-label={fieldConfig?.label || field}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={shownText ? "fs-autosuggest__value" : "fs-autosuggest__placeholder"}>
          {shownText || fieldConfig?.placeHolder || ""}
        </span>
      </button>
      {isOpen && (
        <>
          <div className="fs-overlayBg" onClick={onClose} aria-hidden="true" />
          <div className="autocompletePopupWrapper fs-autosuggest__popup">
            <div className="fs-autosuggest__searchRow">
              <span className="fs-autosuggest__searchIcon" aria-hidden="true" />
              <input
                ref={searchInputRef}
                type="text"
                className="fs-autosuggest__searchInput fontSize14"
                value={query}
                placeholder={fieldConfig?.placeHolder || ""}
                autoComplete="off"
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            {subTitle ? (
              <div className="fs-autosuggest__subTitle fontSize12">
                {transformToHtml(subTitle)}
              </div>
            ) : null}
            <div className="fs-autosuggest__sectionTitle fontSize12">{transformToHtml(title)}</div>
            {isLoading ? <div className="fs-autosuggest__loading fontSize12">Loading...</div> : null}
            <ul className="fs-autosuggest__list" role="listbox">
              {!isLoading &&
                suggestions.map((item, index) => {
                  const iata = normalizeAutosuggestIata(item?.iata);
                  if (!iata) return null;
                  const groupData = Array.isArray(item?.groupData) ? item.groupData : [];
                  const nearbyHeader = item?.extraData?.tag?.nearbyHeader;
                  return (
                    <React.Fragment key={`${iata}-${item?.locusCode || index}`}>
                      <SuggestionRow
                        item={item}
                        displayType={displayType}
                        onSelect={handleSelect}
                      />
                      {groupData.length && nearbyHeader ? (
                        <li className="fs-autosuggest__nearbyHeader" role="presentation">
                          {transformToHtml(nearbyHeader)}
                        </li>
                      ) : null}
                      {groupData.map((nearbyItem, nearbyIndex) => {
                        const nearbyIata = normalizeAutosuggestIata(nearbyItem?.iata);
                        if (!nearbyIata) return null;
                        return (
                          <SuggestionRow
                            key={`${nearbyIata}-${nearbyItem?.locusCode || nearbyIndex}`}
                            item={nearbyItem}
                            displayType={displayType}
                            nearby
                            onSelect={handleSelect}
                          />
                        );
                      })}
                    </React.Fragment>
                  );
                })}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
